import React from 'react';
import { Box, Typography } from '@mui/material';
import PageLayout from '../components/layout/PageLayout';
import { PolicyBlock, PRIVACY_POLICY, PRIVACY_POLICY_UPDATED } from '../content/privacy';
import { BRAND } from '../styles/ui';

const labelSx = {
  color: BRAND.muted,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  fontSize: '0.7rem',
  fontWeight: 600,
  mb: 0.5
};

// The policy's table becomes one card per row, so it reads on a phone
const renderBlock = (block: PolicyBlock, key: number) => {
  switch (block.type) {
    case 'p':
      return (
        <Typography key={key} variant="body2" sx={{ mb: 2 }}>
          {block.text}
        </Typography>
      );
    case 'list':
      return (
        <Box key={key} component="ul" sx={{ pl: 3, mt: 0, mb: 2 }}>
          {block.items.map((item) => (
            <Typography key={item} component="li" variant="body2" sx={{ mb: 1 }}>
              {item}
            </Typography>
          ))}
        </Box>
      );
    case 'table':
      return (
        <Box key={key} sx={{ display: 'flex', flexDirection: 'column', gap: 4, my: 3 }}>
          {block.groups.map(({ group, rows }) => (
            <Box key={group}>
              <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, mb: 2 }}>
                {group}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {rows.map((row) => (
                  <Box
                    key={row.situation}
                    sx={{ backgroundColor: BRAND.inset, border: `1px solid ${BRAND.line}`, borderRadius: 1, p: 3 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                      {row.situation}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: row.information ? '1fr 1.618fr' : '1fr' }, gap: 2 }}>
                      {row.information && (
                        <Box>
                          <Typography sx={labelSx}>Types of information</Typography>
                          <Typography variant="body2">{row.information}</Typography>
                        </Box>
                      )}
                      <Box>
                        <Typography sx={labelSx}>Purpose and lawful basis</Typography>
                        {row.purpose.map((text) => (
                          <Typography key={text} variant="body2" sx={{ mb: 1 }}>
                            {text}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      );
    default:
      return null;
  }
};

const Privacy: React.FC = () => (
  <PageLayout title="Privacy policy" subtitle={`Viviplu Limited. Last updated ${PRIVACY_POLICY_UPDATED}.`}>
    {PRIVACY_POLICY.map((section, index) => (
      <Box component="section" key={section.heading} sx={{ mt: index === 0 ? 0 : 4 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          {section.heading}
        </Typography>
        {section.blocks.map(renderBlock)}
      </Box>
    ))}
  </PageLayout>
);

export default Privacy;
